import { motion, AnimatePresence } from 'framer-motion'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router'
import { languages, languageNames } from '#app/i18n-constants.ts'
import { cn } from '#app/utils/misc.tsx'

export function LanguageSwitcher({ className }: { className?: string }) {
	const { t, i18n } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const [isOpen, setIsOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)

	const currentLanguage = i18n.language as (typeof languages)[number]

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const changeLanguage = async (newLang: (typeof languages)[number]) => {
		if (newLang === currentLanguage) return
		document.cookie = `i18next=${newLang};path=/;max-age=31536000`
		await i18n.changeLanguage(newLang)
		void navigate(location.pathname + location.search, { replace: true })
	}

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="flex h-10 w-10 items-center justify-center transition-opacity hover:opacity-60"
				style={{ color: 'var(--ink)', background: 'none', border: 'none', cursor: 'pointer' }}
				aria-label={t('changeLanguage')}
			>
				<svg
					className="h-[18px] w-[18px]"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
					strokeWidth={1.5}
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
					/>
				</svg>
				<span className="sr-only">
					{languageNames[currentLanguage]}
				</span>
			</button>

			<AnimatePresence>
				{isOpen && (
					<motion.div
						initial={{ opacity: 0, y: 4 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 4 }}
						transition={{ duration: 0.15 }}
						className="absolute right-0 z-50 mt-1 min-w-[180px] origin-top-right overflow-hidden py-1"
						style={{
							background: 'var(--paper)',
							border: '1px solid var(--line)',
							boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
						}}
					>
						{languages.map((lang) => (
							<button
								key={lang}
								onClick={() => {
									void changeLanguage(lang)
									setIsOpen(false)
								}}
								className="flex w-full items-center justify-between px-4 py-2.5 text-[13px] transition-colors"
								style={{
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									fontFamily: 'var(--font-sans)',
									color: currentLanguage === lang ? 'var(--ink)' : 'var(--ink-soft)',
									fontWeight: currentLanguage === lang ? 500 : 400,
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.background = 'var(--paper-2)'
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.background = 'none'
								}}
							>
								<span>{languageNames[lang]}</span>
								{currentLanguage === lang && (
									<span style={{ color: 'var(--ink)', fontSize: 14 }}>✓</span>
								)}
							</button>
						))}
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	)
}
